import { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, Pencil, Trash2, RefreshCw, Video, Film, UploadCloud } from 'lucide-react'
import { Modal, ConfirmModal } from '../../common/Modal/Modal'
import Select from '../../common/Select/Select'
import { fetchWatchBuys, saveWatchBuy, deleteWatchBuy } from './services/watchBuySlice'

const MAX_VIDEO_MB = 50
const MAX_POSTER_MB = 4

/* Video + poster banne ni EXACT size (backend ma AdminWatchBuyController MEDIA_W / MEDIA_H pan same rakhvu) */
const MEDIA_W = 1080
const MEDIA_H = 1920

const EMPTY = {
  id: '', product_id: '', title: '', sort_order: 0,
  show_on_home: true, show_on_product: true, status: true,
}

/* file nu real width x height vanchva mate */
const readVideoSize = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const v = document.createElement('video')
    v.preload = 'metadata'
    v.onloadedmetadata = () => {
      const size = { w: v.videoWidth, h: v.videoHeight }
      URL.revokeObjectURL(url)
      resolve(size)
    }
    v.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read this video. Please use an MP4 or WebM file.'))
    }
    v.src = url
  })

const readImageSize = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const size = { w: img.naturalWidth, h: img.naturalHeight }
      URL.revokeObjectURL(url)
      resolve(size)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read this image.'))
    }
    img.src = url
  })

export default function WatchBuy() {
  const dispatch = useDispatch()
  const { list, products, isLoading, isError, message } = useSelector((s) => s.watchBuy)

  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [editing, setEditing] = useState(null)
  const [active, setActive] = useState(null)
  const [video, setVideo] = useState(null)
  const [poster, setPoster] = useState(null)
  const [removePoster, setRemovePoster] = useState(false)
  const [videoErr, setVideoErr] = useState('')
  const [posterErr, setPosterErr] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const videoRef = useRef(null)
  const posterRef = useRef(null)

  useEffect(() => { dispatch(fetchWatchBuys()) }, [dispatch])

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const productOptions = useMemo(
    () => [
      { value: '', label: '— Select product —' },
      ...(products || []).map((p) => ({ value: p.id, label: p.name })),
    ],
    [products]
  )

  const posterPreview = useMemo(() => (poster ? URL.createObjectURL(poster) : null), [poster])
  useEffect(() => () => { if (posterPreview) URL.revokeObjectURL(posterPreview) }, [posterPreview])

  const resetFiles = () => {
    setVideo(null); setPoster(null); setRemovePoster(false)
    setVideoErr(''); setPosterErr(''); setErr('')
  }

  const openNew = () => {
    setForm(EMPTY); setEditing(null)
    resetFiles(); setModalOpen(true)
  }

  const openEdit = (row) => {
    setForm({
      id: row.id, product_id: row.product_id, title: row.title || '',
      sort_order: row.sort_order, show_on_home: row.show_on_home,
      show_on_product: row.show_on_product, status: row.status,
    })
    setEditing(row)
    resetFiles(); setModalOpen(true)
  }

  const closeModal = () => { if (!saving) setModalOpen(false) }

  /* ── video pick: type + size(MB) + EXACT width x height check ── */
  const pickVideo = async (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    setVideoErr('')

    if (!/^video\/(mp4|webm)$/.test(f.type)) {
      return setVideoErr('Only MP4 or WebM videos are allowed.')
    }
    if (f.size > MAX_VIDEO_MB * 1024 * 1024) {
      return setVideoErr(`Video must be smaller than ${MAX_VIDEO_MB} MB.`)
    }

    try {
      const { w, h } = await readVideoSize(f)
      if (w !== MEDIA_W || h !== MEDIA_H) {
        return setVideoErr(`Video size must be exactly ${MEDIA_W} × ${MEDIA_H} px. Your video is ${w} × ${h} px.`)
      }
      setVideo(f)
    } catch (ex) {
      setVideoErr(ex.message)
    }
  }

  /* ── poster pick: type + size(MB) + EXACT width x height check ── */
  const pickPoster = async (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    setPosterErr('')

    if (!/^image\/(png|jpeg|webp)$/.test(f.type)) {
      return setPosterErr('Only JPG, PNG or WEBP images are allowed.')
    }
    if (f.size > MAX_POSTER_MB * 1024 * 1024) {
      return setPosterErr(`Poster must be smaller than ${MAX_POSTER_MB} MB.`)
    }

    try {
      const { w, h } = await readImageSize(f)
      if (w !== MEDIA_W || h !== MEDIA_H) {
        return setPosterErr(`Poster size must be exactly ${MEDIA_W} × ${MEDIA_H} px. Your image is ${w} × ${h} px.`)
      }
      setPoster(f)
      setRemovePoster(false)
    } catch (ex) {
      setPosterErr(ex.message)
    }
  }

  const submit = async () => {
    setErr('')
    if (!form.product_id) return setErr('Please select a product.')
    if (!editing && !video) return setErr('Please upload a video.')

    const fd = new FormData()
    if (form.id) fd.append('id', form.id)
    fd.append('product_id', form.product_id)
    fd.append('title', form.title || '')
    fd.append('sort_order', form.sort_order || 0)
    fd.append('show_on_home', form.show_on_home ? 1 : 0)
    fd.append('show_on_product', form.show_on_product ? 1 : 0)
    fd.append('status', form.status ? 1 : 0)
    if (video) fd.append('video', video)
    if (poster) fd.append('poster', poster)
    if (removePoster) fd.append('remove_poster', 1)

    setSaving(true)
    try {
      await dispatch(saveWatchBuy(fd)).unwrap()
      setModalOpen(false)
    } catch (ex) {
      setErr(ex?.message || 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async () => {
    const result = await dispatch(deleteWatchBuy(active.id))
    if (result?.meta?.requestStatus === 'fulfilled' || !result?.meta) {
      setDeleteOpen(false)
      setActive(null)
    }
  }

  return (
    <div className="dash-page page-in">
      {/* ── PAGE HEADER ── */}
      <div className="pg-header">
        <h1 className="page-title" style={{ margin: 0 }}>Watch &amp; Buy</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={() => dispatch(fetchWatchBuys())} disabled={isLoading}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={openNew}>
            <Plus size={14} /> Add Video
          </button>
        </div>
      </div>

      {isError && message && <div className="login-error">{message}</div>}

      {/* ── TABLE ── */}
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Video</th><th>Product</th><th>Order</th>
                <th>Home</th><th>Product page</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>Loading…</td></tr>
              )}
              {!isLoading && list.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text3)' }}>
                    <Video size={22} style={{ display: 'block', margin: '0 auto 8px' }} />
                    No videos yet. Click "Add Video" to add one.
                  </td>
                </tr>
              )}
              {!isLoading && list.map((r) => (
                <tr key={r.id}>
                  <td>
                    <video
                      src={r.video_url} poster={r.poster_url || undefined} muted preload="metadata"
                      style={{ width: 48, height: 84, objectFit: 'cover', borderRadius: 8, background: '#000' }}
                    />
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--text1)' }}>{r.title || r.product_name}</div>
                    {r.title && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>{r.product_name}</div>}
                  </td>
                  <td style={{ color: 'var(--text2)' }}>{r.sort_order}</td>
                  <td>
                    <span className={`badge ${r.show_on_home ? 'badge-active' : 'badge-inactive'}`}>
                      {r.show_on_home ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${r.show_on_product ? 'badge-active' : 'badge-inactive'}`}>
                      {r.show_on_product ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${r.status ? 'badge-active' : 'badge-inactive'}`}>
                      {r.status ? 'active' : 'hidden'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openEdit(r)} title="Edit"><Pencil size={14} /></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => { setActive(r); setDeleteOpen(true) }} title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── ADD / EDIT MODAL ── */}
      <Modal
        open={modalOpen}
        title={editing ? 'Edit Video' : 'Add Video'}
        subtitle="Shown in the Watch & Buy section on home and product pages"
        onClose={closeModal}
        footer={(
          <>
            <button className="btn btn-outline" onClick={closeModal} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Update Video' : 'Save Video'}
            </button>
          </>
        )}
      >
        {err && <div className="login-error" style={{ marginBottom: 12 }}>{err}</div>}

        <div className="form-grid cols-2">
          <div className="form-field">
            <label>Product</label>
            <Select
              value={form.product_id}
              onChange={(v) => set('product_id', v)}
              options={productOptions}
              searchable={productOptions.length > 8}
              placeholder="— Select product —"
            />
          </div>
          <div className="form-field">
            <label>Sort Order</label>
            <input
              className="input" type="number" min={0} max={9999}
              value={form.sort_order} onChange={(e) => set('sort_order', e.target.value)}
            />
          </div>

          <div className="form-field full">
            <label>Title <small style={{ color: 'var(--text3)' }}>(optional · product name is shown if left empty)</small></label>
            <input
              className="input" maxLength={191} placeholder="e.g. Styling our tote bag"
              value={form.title} onChange={(e) => set('title', e.target.value)}
            />
          </div>

          {/* ---- Video ---- */}
          <div className="form-field">
            <label>
              Video <small style={{ color: 'var(--text3)' }}>(mp4 / webm · exactly {MEDIA_W} × {MEDIA_H} px · max {MAX_VIDEO_MB}MB)</small>
            </label>
            <input
              ref={videoRef} type="file" accept="video/mp4,video/webm" hidden
              onChange={pickVideo}
            />
            <div className="prd-file-row">
              <button type="button" className="btn btn-outline btn-sm" onClick={() => videoRef.current?.click()}>
                <Film size={13} /> {video || editing ? 'Replace video' : 'Upload video'}
              </button>
              {video && (
                <>
                  <span className="prd-file-name">
                    {video.name} ({(video.size / 1024 / 1024).toFixed(1)} MB)
                  </span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setVideo(null)}>Cancel new</button>
                </>
              )}
              {!video && editing?.video_url && (
                <a href={editing.video_url} target="_blank" rel="noreferrer" className="prd-file-name">Current video</a>
              )}
            </div>
            {videoErr && <small style={{ color: '#ef4444' }}>{videoErr}</small>}
            {editing && !video && !videoErr && (
              <small style={{ color: 'var(--text3)' }}>Leave empty to keep the current video.</small>
            )}
          </div>

          {/* ---- Poster ---- */}
          <div className="form-field">
            <label>
              Poster Image <small style={{ color: 'var(--text3)' }}>(optional · exactly {MEDIA_W} × {MEDIA_H} px · JPG, PNG, WEBP · max {MAX_POSTER_MB}MB)</small>
            </label>
            <input
              ref={posterRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
              onChange={pickPoster}
            />
            <div className="prd-file-row">
              <button type="button" className="btn btn-outline btn-sm" onClick={() => posterRef.current?.click()}>
                <UploadCloud size={13} /> {poster || (editing?.poster_url && !removePoster) ? 'Replace poster' : 'Upload poster'}
              </button>
              {poster && (
                <>
                  <span className="prd-file-name">{poster.name}</span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPoster(null)}>Cancel new</button>
                </>
              )}
              {!poster && editing?.poster_url && !removePoster && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRemovePoster(true)}>Remove</button>
              )}
              {!poster && editing?.poster_url && removePoster && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRemovePoster(false)}>Undo remove</button>
              )}
            </div>
            {posterErr && <small style={{ color: '#ef4444' }}>{posterErr}</small>}
            {(posterPreview || (editing?.poster_url && !removePoster)) && (
              <img
                className="wb-poster-preview"
                src={posterPreview || editing.poster_url}
                alt="Poster preview"
              />
            )}
          </div>

          {/* ---- Visibility ---- */}
          <div className="form-field full">
            <label style={{ fontWeight: 600 }}>Show as</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <label>
                <input type="checkbox" checked={form.show_on_home} onChange={(e) => set('show_on_home', e.target.checked)} />{' '}
                Show on home page
              </label>
              <label>
                <input type="checkbox" checked={form.show_on_product} onChange={(e) => set('show_on_product', e.target.checked)} />{' '}
                Show on product page
              </label>
              <label>
                <input type="checkbox" checked={form.status} onChange={(e) => set('status', e.target.checked)} />{' '}
                Active
              </label>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete video"
        message={`Delete "${active?.title || active?.product_name || 'selected video'}"? The video and poster will also be removed.`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page { display: flex; flex-direction: column; gap: var(--space-lg); width: 100%; }

        .pg-header {
          display: flex; align-items: center; justify-content: space-between;
          gap: var(--space-md); flex-wrap: wrap;
        }

        .login-error {
          background: rgba(239, 68, 68, 0.12);
          border: 1px solid rgba(239, 68, 68, 0.35);
          color: #fca5a5; font-size: 0.875rem;
          padding: 0.6rem 0.9rem; border-radius: 10px;
        }

        .prd-file-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; min-height: 36px; }
        .prd-file-name {
          font-size: var(--fs-xs); color: var(--text2); overflow: hidden;
          text-overflow: ellipsis; max-width: 180px; white-space: nowrap;
        }

        .wb-poster-preview {
          width: 72px; height: 128px; object-fit: cover; margin-top: 8px;
          border-radius: var(--radius-md); border: 1px solid var(--border);
          background: var(--bg-raised);
        }
      `}</style>
    </div>
  )
}